import React from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'

import { ProductImage } from 'app/common/Common'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { DownloadIcon } from 'client/utils/icons/download-icon'
import FilterLinesIcon from 'client/utils/icons/filter-lines-icon'
import {
  DcBreadcrumbs,
  DcButton,
  DcDropDown,
  DcFlexBox,
  PixelDropDownMenu,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionContent,
  DcOverlaySlider,
  colors,
  DcDiv,
} from 'pixel-base'
import queryString from 'query-string'
import { getWarehouseInventory } from 'utils/api-routes-inventory'
import { exportInventory, get, getCategories } from 'utils/api-routes.util'
import ArrowRightFromBracketIcon from 'utils/icons/arrow-right-from-bracket-icon'
import UploadIcon from 'utils/icons/upload-icon'
import InventoryItems from './InventoryItems'
import InventoryProductSearch from './InventoryProductSearch'
import InventoryUploadModal from './InventoryUploadModal'
import {
  CenteredInventoryCellValue,
  InventoryProductNameCell,
  InventorySkuCell,
  inventoryFitWidthTableStyles,
} from './WarehouseInventoryProductCell'
import { ICollection } from '../../../../../../decl'

const columns = [
  {
    dataKey: 'product_image',
    name: 'Image',
    width: '8%',
    center: true,
    selector: (row) => (
      <CenteredInventoryCellValue>
        <ProductImage
          style={{
            width: '32px',
            marginRight: 0,
            flexShrink: 0,
          }}
          path={row.product_image}
        />
      </CenteredInventoryCellValue>
    ),
  },
  {
    dataKey: 'product_name',
    name: 'Product Name',
    width: '24%',
    sortable: true,
    selector: (row) => <InventoryProductNameCell name={row.product_name} />,
  },
  {
    dataKey: 'product_sku',
    name: 'SKU',
    width: '14%',
    sortable: true,
    selector: (row) => <InventorySkuCell sku={row.product_sku} />,
  },
  {
    dataKey: 'column_gap',
    name: '',
    width: '5%',
    sortable: false,
    selector: () => null,
  },
  {
    dataKey: 'unavailable',
    name: 'Unavailable',
    width: '9%',
    sortable: true,
    center: true,
    selector: (row) => (
      <CenteredInventoryCellValue>
        {row.warehouse_inventory?.unavailable ?? '--'}
      </CenteredInventoryCellValue>
    ),
  },
  {
    dataKey: 'committed',
    name: 'Committed',
    width: '9%',
    sortable: true,
    center: true,
    selector: (row) => (
      <CenteredInventoryCellValue>
        {row.warehouse_inventory?.committed ?? '--'}
      </CenteredInventoryCellValue>
    ),
  },
  {
    dataKey: 'available',
    name: 'Available',
    width: '9%',
    sortable: true,
    center: true,
    selector: (row) => (
      <CenteredInventoryCellValue>
        {row.warehouse_inventory?.available ?? '--'}
      </CenteredInventoryCellValue>
    ),
  },
  {
    dataKey: 'on_hand',
    name: 'On Hand',
    width: '9%',
    sortable: true,
    center: true,
    selector: (row) => (
      <CenteredInventoryCellValue>
        {row.warehouse_inventory?.on_hand ?? '--'}
      </CenteredInventoryCellValue>
    ),
  },
  {
    dataKey: 'purchase_price',
    name: 'Purchase Price',
    width: '13%',
    sortable: false,
    center: true,
    selector: (row) => (
      <CenteredInventoryCellValue>
        {row.purchase_price != null ? row.purchase_price : '--'}
      </CenteredInventoryCellValue>
    ),
  },
]

interface Filters {
  warehouse_id: string | null
  page_size: number
  page: number
  search: string
  searchBySKU: boolean
  sortKey?: string
  sortOrder?: string
  collection_id: string | null
}

const WarehouseWiseInventory = () => {
  const navigate = useNavigate()
  const [inventory, setInventory] = React.useState([])
  interface Warehouse {
    wh_id: string
    name: string
  }
  const [warehousesList, setWarehousesList] = React.useState<Warehouse[]>([])
  const [totalRows, setTotalRows] = React.useState(0)
  const [uploadInventoryModal, setUploadInventoryModal] = React.useState(false)
  const [showHistoryModal, setShowHistoryModal] = React.useState(false)
  const [detailData, setDetailData] = React.useState<any>(null)
  const [showFilters, setShowFilters] = React.useState(false)
  const [categories, setCategories] = React.useState<ICollection[]>([])
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = React.useState<string | null>(null)

  // Ref to track if warehouses have been fetched to prevent duplicate calls
  const warehousesFetched = React.useRef(false)

  // Ref to track the last successful API call to prevent unnecessary re-fetches
  const lastSuccessfulCall = React.useRef<string>('')

  // Ref to prevent multiple simultaneous API calls
  const isFetching = React.useRef(false)

  const [loading, setLoading] = React.useState({
    warehouses: false,
    inventory: false,
    table: false,
  })
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)

  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState<Filters>({
    warehouse_id: (p.warehouse_id as string) || null,
    page_size: Number(p.page_size) || 50,
    page: Number(p.page) || 0,
    search: (p.search as string) || '',
    searchBySKU: p.searchBySKU === '1',
    collection_id: (p.collection_id as string) || 'null',
    sortKey: (p.sortKey as string) || '',
    sortOrder: (p.sortOrder as string) || '',
  })
  const entityType = (() => {
    const raw = ((p.type as string) || 'warehouse').toLowerCase()
    // Normalize common variants like "market-place", "market place", typos etc.
    return raw.includes('market') ? 'marketplace' : 'warehouse'
  })()
  const breadcrumbs = [
    {
      name: entityType === 'warehouse' ? 'Warehouse List' : 'Marketplace List',
      link: '/inventory',
    },
    {
      name:
        entityType === 'warehouse'
          ? 'Warehouse Inventory'
          : 'Marketplace Inventory',
      active: true,
    },
  ]
  const readData = async (currentFilters) => {
    // Prevent multiple simultaneous API calls
    if (loading.inventory || isFetching.current) return

    // Create a unique key for this request to prevent duplicate calls
    const requestKey = JSON.stringify({
      warehouse_id: currentFilters.warehouse_id,
      page: currentFilters.page,
      page_size: currentFilters.page_size,
      search: currentFilters.search,
      searchBySKU: currentFilters.searchBySKU,
      sortKey: currentFilters.sortKey,
      sortOrder: currentFilters.sortOrder,
      collection_id: currentFilters.collection_id,
    })

    // Skip if this exact request was already made successfully
    if (lastSuccessfulCall.current === requestKey) {
      return
    }

    isFetching.current = true
    setLoading((prev) => ({ ...prev, inventory: true, table: true }))

    getWarehouseInventory(currentFilters).subscribe({
      next: (res) => {
        // Clear any previous errors on success
        setError(null)

        // Track this successful API call to prevent duplicates
        lastSuccessfulCall.current = requestKey

        setInventory((res.response as any).data)
        setTotalRows((res.response as any)._meta.total)
        setLoading((prev) => ({ ...prev, inventory: false, table: false }))
        isFetching.current = false
      },
      error: (err) => {
        console.error('Inventory API failed:', err)
        // Stop all loaders on error
        setLoading((prev) => ({ ...prev, inventory: false, table: false }))
        isFetching.current = false
        // Clear inventory data on error
        setInventory([])
        setTotalRows(0)
        // Set user-friendly error message
        setError(
          'Failed to load inventory data. The server may be experiencing issues. Please try again later.',
        )
        // You can add a toast notification here if you have a toast system
        // showToast('error', 'Failed to load inventory data. Please try again.')
      },
    })
  }

  const getAllCategories = () => {
    getCategories().subscribe((res) => {
      const response = res.response as any as { data: ICollection[] }
      setCategories(response.data)
    })
  }

  const getAllWarehouses = () => {
    // Prevent multiple simultaneous warehouse API calls
    if (
      loading.warehouses ||
      warehousesList.length > 0 ||
      warehousesFetched.current
    ) {
      return
    }

    warehousesFetched.current = true
    setLoading((prev) => ({ ...prev, warehouses: true }))
    const endpoint =
      entityType === 'marketplace' ? '/market-places' : '/warehouses'
    get(endpoint).subscribe({
      next: (res) => {
        if (!warehousesList.length) {
          setWarehousesList((res.response as any).data)
        }
        setLoading((prev) => ({ ...prev, warehouses: false }))
      },
      error: () => {
        warehousesFetched.current = false // Reset on error
        setLoading((prev) => ({ ...prev, warehouses: false }))
      },
    })
  }

  const exportReport = (exportFilters) => {
    exportInventory({ ...exportFilters, page_size: 10000 })
  }

  // Consolidated effect for warehouses and initial setup
  React.useEffect(() => {
    // Only fetch warehouses/market places if we don't have them and not already loading
    if (!warehousesList.length && !loading.warehouses) {
      getAllWarehouses()
    }
  }, []) // Only run once on mount

  // Effect for setting default warehouse and navigation
  React.useEffect(() => {
    if (warehousesList.length && !filters.warehouse_id) {
      const firstId =
        (warehousesList[0] as any).wh_id || (warehousesList[0] as any).id
      setFilters((prev) => ({
        ...prev,
        warehouse_id: firstId,
      }))
    }
  }, [warehousesList, filters.warehouse_id])

  // Combined effect for navigation and data fetching to prevent infinite loops
  React.useEffect(() => {
    if (filters.warehouse_id && !loading.inventory && !loading.warehouses) {
      // Create a unique key for this request to prevent duplicate calls
      const requestKey = JSON.stringify({
        warehouse_id: filters.warehouse_id,
        page: filters.page,
        page_size: filters.page_size,
        search: filters.search,
        searchBySKU: filters.searchBySKU,
        sortKey: filters.sortKey,
        sortOrder: filters.sortOrder,
        collection_id: filters.collection_id,
      })

      // Skip if this exact request was already made successfully
      if (lastSuccessfulCall.current === requestKey) {
        return
      }

      // Skip if we're already fetching to prevent concurrent requests
      if (isFetching.current) {
        return
      }

      // Update URL and fetch data together to prevent double triggers
      navigate({
        pathname: '/inventory/warehouse-wise',
        search: queryString.stringify({
          ...filters,
          searchBySKU: filters.searchBySKU ? 1 : undefined,
          ...(entityType === 'marketplace' ? { type: 'marketplace' } : {}),
        }),
      })

      readData(filters)
    }
  }, [
    filters.warehouse_id,
    filters.page,
    filters.page_size,
    filters.search,
    filters.searchBySKU,
    filters.sortKey,
    filters.sortOrder,
    filters.collection_id,
    loading.inventory,
    loading.warehouses,
  ])

  // Effect for categories (only once on mount)
  React.useEffect(() => {
    getAllCategories()
  }, [])

  const handlePageChange = (page) => {
    if (filters.page !== page - 1) setFilters({ ...filters, page: page - 1 })
  }

  const handlePerRowsChange = (newPerPage) => {
    setFilters({ ...filters, page_size: newPerPage })
  }

  const inventorySearchBar = (
    <InventoryProductSearch
      search={filters.search}
      searchBySKU={filters.searchBySKU}
      onSearchModeChange={(searchBySKU) =>
        setFilters({
          ...filters,
          searchBySKU,
        })
      }
      onSearchChange={(search) => {
        setResetPaginationToggle(search.length > 0)
        setFilters({ ...filters, search })
      }}
    />
  )

  const UpdateOptions = [
    {
      label: 'Upload Inventory',
      clickHandler: () => setUploadInventoryModal(true),
    },
    {
      label: 'Manual Inventory Update',
      clickHandler: () =>
        navigate({
          pathname: '/inventory/edit',
          search: queryString.stringify(filters),
        }),
    },
    {
      label: 'Add Purchase',
      clickHandler: () =>
        navigate({
          pathname: '/inventory/purchases',
          search: queryString.stringify(filters),
        }),
    },
  ]

  return (
    <>
      <DcFlexBox
        height="60px"
        style={{
          padding: `8px 20px`,
          borderBottom: `1px solid ${colors.grayBorderLight}`,
          fontSize: '16px',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          style={{ padding: '.5rem 0px' }}
        >
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />

          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer
        style={{ padding: 0, minHeight: 'calc(100vh - 60px)' }}
      >
        <Section style={{ padding: 0 }}>
          <SectionContent style={{ padding: 0, borderRadius: 0 }}>
            <DcFlexBox
              style={{ padding: '5px 20px' }}
              justifyContent="space-between"
              alignItems="center"
            >
              {isMobile ? (
                <DcFlexBox
                  position="relative"
                  alignItems="center"
                  gap="5px"
                  height="auto"
                >
                  {inventorySearchBar}

                  <DcDiv position="absolute" right="0px" top="2px">
                    <DcButton
                      variant="link"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FilterLinesIcon size={16} />
                    </DcButton>
                  </DcDiv>
                </DcFlexBox>
              ) : (
                <>
                  <DcFlexBox justifyContent="flex-end" gap="5px" width="70%">
                    <DcDropDown
                      dropdownSize="sm"
                      required
                      placeholder={
                        entityType === 'marketplace'
                          ? 'Select Market Place'
                          : 'Select Warehouse'
                      }
                      options={warehousesList.map((w: any) => ({
                        label: w.name,
                        value: w.wh_id || w.id,
                      }))}
                      value={filters.warehouse_id}
                      onChange={(e) => {
                        setFilters({
                          ...filters,
                          warehouse_id: e.target.value,
                        })
                      }}
                    />
                    {entityType === 'warehouse' && (
                      <DcDropDown
                        dropdownSize="sm"
                        required
                        placeholder="Select Collection"
                        customLabel=""
                        options={[
                          {
                            collection_name: 'All Collections',
                            id: null,
                          },
                          ...categories,
                        ].map((item: any) => {
                          if (item.collection_name === 'All Collections') {
                            return {
                              label: item.collection_name,
                              value: '',
                            }
                          } else {
                            return {
                              label: item.collection_id?.source_collection_name,
                              value: item.collection_id?.id?.toString() || '',
                            }
                          }
                        })}
                        value={filters.collection_id || ''}
                        onChange={(e) => {
                          setFilters({
                            ...filters,
                            collection_id: e.target.value || null,
                          })
                        }}
                      />
                    )}
                    {inventorySearchBar}
                  </DcFlexBox>
                  <DcFlexBox justifyContent="flex-end" gap="5px">
                    <PixelDropDownMenu
                      variant="outline"
                      size="sm"
                      options={UpdateOptions}
                      toggleText={
                        <>
                          <DownloadIcon size={16} /> <span>Stock In</span>
                        </>
                      }
                    />
                    <DcButton
                      size="sm"
                      variant="outline-primary"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                      }}
                      onClick={() => {
                        navigate('/inventory/uploads')
                      }}
                    >
                      <UploadIcon size={16} />
                      <span>Upload History</span>
                    </DcButton>

                    <DcButton
                      withIcon={true}
                      onClick={() => {
                        exportReport(filters)
                      }}
                      variant="outline-secondary"
                      size="sm"
                    >
                      <ArrowRightFromBracketIcon size={16} />
                      <span>Export</span>
                    </DcButton>
                  </DcFlexBox>
                </>
              )}
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent style={{ padding: '10px' }}>
            <PixelTable
              pagination={true}
              paginationServer
              columns={columns}
              data={inventory || []}
              customStyles={inventoryFitWidthTableStyles}
              highlightOnHover={true}
              fixedHeader
              fixedHeaderScrollHeight={
                isMobile ? 'calc(100vh - 230px)' : 'calc(100vh - 218px)'
              }
              pointerOnHover
              paginationPerPage={filters.page_size}
              progressPending={loading.table}
              onChangeRowsPerPage={handlePerRowsChange}
              onRowClicked={(row) => {
                setShowHistoryModal(true)
                setDetailData(row)
              }}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
              onChangePage={handlePageChange}
              paginationTotalRows={totalRows}
              paginationDefaultPage={Number(filters.page || 0) + 1}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
      <DcOverlaySlider
        width="30%"
        title={'Bulk stock update from file'}
        isOpen={uploadInventoryModal}
        onClose={() => setUploadInventoryModal(false)}
      >
        {uploadInventoryModal && (
          <SectionContent>
            <InventoryUploadModal
              warehousesList={warehousesList}
              setUploadInventoryModal={setUploadInventoryModal}
              handleClose={(status) => {
                if (status === 'success') readData(filters)
                setUploadInventoryModal(false)
              }}
            />
          </SectionContent>
        )}
      </DcOverlaySlider>

      <DcOverlaySlider
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title="History"
        width="70%"
        padding="0px"
      >
        <InventoryItems
          data={{
            product_id: detailData?.product_id,
            warehouse_id: Number(filters.warehouse_id) || 0,
          }}
        />
      </DcOverlaySlider>
      <DcOverlaySlider
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filters"
        width="70%"
        customWith="70%"
        padding="0px"
      >
        <DcFlexBox padding="10px" flexDirection="column" alignItems="center">
          <DcFlexBox
            flexDirection="column"
            justifyContent="flex-end"
            gap="5px"
            height="auto"
          >
            <DcDropDown
              dropdownSize="sm"
              required
              placeholder={
                entityType === 'marketplace'
                  ? 'Select Market Place'
                  : 'Select Warehouse'
              }
              options={warehousesList.map((w: any) => ({
                label: w.name,
                value: w.wh_id || w.id,
              }))}
              value={filters.warehouse_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  warehouse_id: e.target.value,
                })
              }}
            />
            <DcDropDown
              dropdownSize="sm"
              required
              placeholder="Select Collection"
              customLabel=""
              options={[
                {
                  collection_name: 'All Collections',
                  id: null,
                },
                ...categories,
              ].map((item: any) => {
                if (item.collection_name === 'All Collections') {
                  return {
                    label: item.collection_name,
                    value: '',
                  }
                } else {
                  return {
                    label: item.collection_id?.source_collection_name,
                    value: item.collection_id?.id?.toString() || '',
                  }
                }
              })}
              value={filters.collection_id || ''}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  collection_id: e.target.value || null,
                })
              }}
            />
          </DcFlexBox>
          <DcFlexBox
            height="auto"
            flexDirection="column"
            justifyContent="flex-end"
            gap="5px"
          >
            <PixelDropDownMenu
              variant="outline"
              size="sm"
              options={UpdateOptions}
              toggleText={
                <>
                  <DownloadIcon size={16} /> <span>Stock In</span>
                </>
              }
            />
            <DcButton
              size="sm"
              variant="outline-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
              }}
              onClick={() => {
                navigate('/inventory/uploads')
              }}
            >
              <UploadIcon size={16} />
              <span>Upload History</span>
            </DcButton>

            <DcButton
              withIcon={true}
              onClick={() => {
                exportReport(filters)
              }}
              variant="outline-secondary"
              size="sm"
            >
              <ArrowRightFromBracketIcon size={16} />
              <span>Export</span>
            </DcButton>
          </DcFlexBox>
        </DcFlexBox>
      </DcOverlaySlider>
    </>
  )
}

export default WarehouseWiseInventory
